import { InputFormat, OutputFormat } from "./types";

export function fileTypeToPandocType(type: string): InputFormat {
    switch(type) {
        case "adoc":
        case "asc":
        case "asciidoc":
            return "asciidoc"
        case "text/x-tex":
            return "latex"
        case "application/x-docbook+xml":
        case "application/xml":
            return "docbook"
        case "text/html":
            return "html"
        case "md":
        case "text/markdown":
            return "markdown"
        case "application/vnd.oasis.opendocument.text":
            return "odt"
        case "application/epub+zip":
            return "epub"
        case "application/rtf":
            return "rtf"
        case "application/vnd.openxmlformats-officedocument.presentationml.presentation":
            return "pptx"
        case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
            return "docx"
    }
    throw new Error("This type is not supported by Pandoc")
}

export function isBinary(type: InputFormat): boolean {
    switch(type) {
        case "asciidoc":
        case "bibtex":
        case "biblatex":
        case "bits": // not sure
        case "commonmark":
        case "commonmark_x":
        case "creole":
        case "csljson":
        case "csv":
        case "tsv":
        case "djot":
        case "docbook":
        case "dokuwiki":
        case "endnotexml":
        case "fb2": // not sure
        case "gfm":
        case "haddock":
        case "html":
        case "ipynb":
        case "jats":
        case "jira": // not sure
        case "json":
        case "latex":
        case "markdown":
        case "markdown_mmd":
        case "markdown_phpextra":
        case "markdown_strict":
        case "mediawiki":
        case "man":
        case "mdoc": // not sure
        case "muse":
        case "native":
        case "opml": // not sure
        case "org":
        case "pod": // not sure
        case "ris": // not sure
        case "rst":
        case "t2t":
        case "textile":
        case "tikiwiki":
        case "twiki":
        case "typst":
        case "vimwiki":
        case "xml":
            return false;
        case "docx":
        case "rtf":
        case "odt":
        case "pptx":
        case "epub":
        case "xlsx":
            return true;
    }
}

export const outputFormatsLabels: Record<OutputFormat, {label: string, ext: string}> = {
    "ansi": { label: "ANSI", ext: "ans" },
    "asciidoc": { label: "AsciiDoc", ext: "adoc" },
    "asciidoc_legacy": { label: "AsciiDoc – legacy", ext: "adoc" },
    "asciidoctor": { label: "AsciiDoctor", ext: "adoc" },
    "bbcode": { label: "BBCode", ext: "bbcode" },
    "bbcode_fluxbb": { label: "BBCode – FluxBB", ext: "bbcode" },
    "bbcode_phpbb": { label: "BBCode – PHPBB", ext: "bbcode" },
    "bbcode_steam": { label: "BBCode – Steam", ext: "bbcode" },
    "bbcode_hubzilla": { label: "BBCode – HubZilla", ext: "bbcode" },
    "bbcode_xenforo": { label: "BBCode – Xenforo", ext: "bbcode" },
    "beamer": { label: "Beamer", ext: "tex" },
    "bibtex": { label: "BibTeX", ext: "bib" },
    "biblatex": { label: "BibLaTeX", ext: "bib" },
    "chunkedhtml": { label: "Chunked HTML", ext: "html" },
    "commonmark": { label: "CommonMark", ext: "md" },
    "commonmark_x": { label: "CommonMark X", ext: "md" },
    "context": { label: "ConTeXt", ext: "tex" },
    "csljson": { label: "CSL JSON", ext: "json" },
    "djot": { label: "djot", ext: "djot" },
    "docbook": { label: "Docbook", ext: "dbk" },
    "docbook5": { label: "Docbook 5", ext: "dbk" },
    "docx": { label: "Microsoft Word", ext: "docx" },
    "dokuwiki": { label: "DokuWiki", ext: "txt" },
    "epub": { label: "EPUB", ext: "epub" },
    "epub2": { label: "EPUB2", ext: "epub" },
    "fb2": { label: "FB2", ext: "fb2" },
    "gfm": { label: "GitHub Flavored Markdown", ext: "md" },
    "haddock": { label: "Haddock", ext: "txt" },
    "html": { label: "HTML", ext: "html" },
    "html4": { label: "HTML4", ext: "html" },
    "icml": { label: "ICML", ext: "icml" },
    "ipynb": { label: "Jupyter Notebook", ext: "ipynb" },
    "jats_archiving": { label: "JATS – archiving", ext: "xml" },
    "jats_articleauthoring": { label: "JATS – article authoring", ext: "xml" },
    "jats_publishing": { label: "JATS – publishing", ext: "xml" },
    "jats": { label: "JATS", ext: "xml" },
    "jira": { label: "Jira", ext: "txt" },
    "json": { label: "Pandoc AST – JSON", ext: "json" },
    "latex": { label: "LaTeX", ext: "tex" },
    "man": { label: "Man", ext: "man" },
    "markdown": { label: "Markdown", ext: "md" },
    "markdown_mmd": { label: "MultiMarkdown", ext: "md" },
    "markdown_phpextra": { label: "Markdown – PHP Extra", ext: "md" },
    "markdown_strict": { label: "Markdown – Strict", ext: "md" },
    "markua": { label: "Markua", ext: "markua" },
    "mediawiki": { label: "MediaWiki", ext: "wiki" },
    "ms": { label: "roff ms", ext: "ms" },
    "muse": { label: "Emacs Muse", ext: "muse" },
    "native": { label: "Pandoc AST – native", ext: "native" },
    "odt": { label: "Open Document", ext: "odt" },
    "opml": { label: "OPML", ext: "opml" },
    "opendocument": { label: "Open Document", ext: "xml" },
    "org": { label: "Org", ext: "org" },
    "plain": { label: "Plain text", ext: "txt" },
    "pptx": { label: "PPTX", ext: "pptx" },
    "rst": { label: "reStructuredText", ext: "rst" },
    "rtf": { label: "Rich Text Format", ext: "rtf" },
    "texinfo": { label: "Texinfo", ext: "texi" },
    "textile": { label: "Textile", ext: "textile" },
    "slideous": { label: "Slideous", ext: "html" },
    "slidy": { label: "Slidy", ext: "html" },
    "dzslides": { label: "DZSlides", ext: "html" },
    "revealjs": { label: "Reveal.js", ext: "md" },
    "s5": { label: "S5", ext: "html" },
    "tei": { label: "Text Encoding Initiative", ext: "tei" },
    "typst": { label: "Typst", ext: "typst" },
    "vimdoc": { label: "Vimdox", ext: "txt" },
    "xml": { label: "Pandoc AST – XML", ext: "xml" },
    "xwiki": { label: "XWiki", ext: "xwiki" },
    "zimwiki": { label: "ZimWiki", ext: "zimwiki" },
}